import type { FormatType } from './useRichTextEditor';
import { Editor, textblockTypeInputRule } from '@tiptap/core';
import Bold from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import Document from '@tiptap/extension-document';
import Heading from '@tiptap/extension-heading';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import {
  HEADING_CLASS_NAME,
  PARAGRAPGH_CLASS_NAME,
  BOLD_CLASS_NAME,
  UNORDERED_LIST_CLASS_NAME,
  ORDERED_LIST_CLASS_NAME,
  LIST_ITEM_CLASS_NAME,
  LINK_CLASS_NAME,
  IMAGE_CLASS_NAME,
} from './constant';

export const isDisabled = (
  formatTypes: FormatType[],
  activationFunction: (formatType: FormatType) => boolean | undefined
) => {
  return formatTypes.reduce((accumulator: boolean, formatType: FormatType) => {
    return accumulator || activationFunction(formatType) || false;
  }, false);
};

const CustomHeading = Heading.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Alt-1': () => this.editor.commands.toggleHeading({ level: 3 }),
      'Mod-Alt-2': () => this.editor.commands.toggleHeading({ level: 4 }),
      'Mod-Alt-3': () => this.editor.commands.toggleHeading({ level: 5 }),
    };
  },
  addInputRules() {
    return this.options.levels.map((level) => {
      return textblockTypeInputRule({
        find: new RegExp(`^(#{1,${level - 2}})\\s$`),
        type: this.type,
        getAttributes: {
          level,
        },
      });
    });
  },
});

export const BASE_EXTENSIONS = [
  Document,
  Text,
  CustomHeading.configure({
    levels: [3, 4, 5],
    HTMLAttributes: {
      class: HEADING_CLASS_NAME,
    },
  }),
  Paragraph.configure({
    HTMLAttributes: {
      class: PARAGRAPGH_CLASS_NAME,
    },
  }),
  Bold.configure({
    HTMLAttributes: {
      class: BOLD_CLASS_NAME,
    },
  }),
  BulletList.configure({
    HTMLAttributes: {
      class: UNORDERED_LIST_CLASS_NAME,
    },
  }),
  OrderedList.configure({
    HTMLAttributes: {
      class: ORDERED_LIST_CLASS_NAME,
    },
  }),
  ListItem.configure({
    HTMLAttributes: {
      class: LIST_ITEM_CLASS_NAME,
    },
  }),

  Link.configure({
    HTMLAttributes: {
      class: LINK_CLASS_NAME,
      // Change rel to different value
      // Allow search engines to follow links(remove nofollow)
      rel: 'noopener noreferrer',
      validate: (href) => /^https?:\/\//.test(href),
    },
  }),
  Image.configure({
    HTMLAttributes: {
      class: IMAGE_CLASS_NAME,
    },
  }),
];

export const htmlToText = (value: string) => {
  if (typeof window === 'undefined') return '';

  const editor = new Editor({
    content: value,
    extensions: BASE_EXTENSIONS,
  });

  return editor.getText();
};
